import { useEffect } from 'react';
import {
  useShadowRoot,
  useShadowRootElements,
} from '@backstage/plugin-techdocs-react';

const EXTERNAL_LINK_REL = 'noopener noreferrer';
const TECHDOCS_CANVAS_STYLE_ID = 'vwtech-techdocs-canvas-styles';

const TECHDOCS_CANVAS_CSS = `
  html,
  body,
  main,
  article,
  section,
  .md-grid,
  .md-main,
  .md-container,
  .md-content,
  .md-content__inner,
  .md-sidebar,
  .md-sidebar__inner,
  .md-sidebar__scrollwrap,
  .md-nav,
  .md-typeset,
  [data-md-component="main"],
  [data-md-component="container"],
  [data-md-component="content"],
  [data-md-component="sidebar"],
  [data-md-component="toc"] {
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
  }

  .md-main__inner {
    background: rgba(17, 25, 46, 0.82) !important;
    border-radius: 24px !important;
    overflow: hidden !important;
  }

  .md-nav__title,
  [data-md-component="sidebar"] .md-nav__title,
  [data-md-component="toc"] .md-nav__title {
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
    box-shadow: none !important;
    border: 0 !important;
  }

  /* mkdocs-material pins the sidebar titles while their list scrolls and relies
     on an opaque background to hide the items passing underneath. With the
     background cleared above the items show through, so the titles scroll
     with the list instead. */
  [data-md-component="sidebar"] .md-nav__title,
  [data-md-component="toc"] .md-nav__title {
    position: static !important;
  }

  @media (max-width: 76.1875em) {
    .md-main__inner {
      border-radius: 20px !important;
    }
  }
`;

function ensureTechDocsCanvasStyles(
  shadowRoot: ShadowRoot | null | undefined,
): void {
  if (!shadowRoot || shadowRoot.getElementById(TECHDOCS_CANVAS_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = TECHDOCS_CANVAS_STYLE_ID;
  style.textContent = TECHDOCS_CANVAS_CSS;
  shadowRoot.appendChild(style);
}

function getResolvedImageSource(image: HTMLImageElement): string {
  return image.currentSrc || image.src;
}

function configureImageAnchor(anchor: HTMLAnchorElement, href: string): void {
  anchor.href = href;
  anchor.target = '_blank';
  anchor.rel = EXTERNAL_LINK_REL;
}

export function normalizeTechDocsImageLinks(images: HTMLImageElement[]): void {
  for (const image of images) {
    const resolvedSrc = getResolvedImageSource(image);

    if (!resolvedSrc) {
      continue;
    }

    const directAnchorParent =
      image.parentElement instanceof HTMLAnchorElement
        ? image.parentElement
        : null;

    if (directAnchorParent) {
      configureImageAnchor(directAnchorParent, resolvedSrc);
      continue;
    }

    const ownerDocument = image.ownerDocument;
    const parentNode = image.parentNode;

    if (!ownerDocument || !parentNode) {
      continue;
    }

    const anchor = ownerDocument.createElement('a');
    configureImageAnchor(anchor, resolvedSrc);
    parentNode.insertBefore(anchor, image);
    anchor.appendChild(image);
  }
}

function bindTechDocsImageClicks(images: HTMLImageElement[]): () => void {
  const cleanups: Array<() => void> = [];

  for (const image of images) {
    const resolvedSrc = getResolvedImageSource(image);

    if (!resolvedSrc) {
      continue;
    }

    image.style.cursor = 'pointer';

    const directAnchorParent =
      image.parentElement instanceof HTMLAnchorElement
        ? image.parentElement
        : null;

    if (directAnchorParent) {
      configureImageAnchor(directAnchorParent, resolvedSrc);

      const handleAnchorClick = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        window.open(resolvedSrc, '_blank', 'noopener,noreferrer');
      };

      directAnchorParent.addEventListener('click', handleAnchorClick);
      cleanups.push(() => {
        directAnchorParent.removeEventListener('click', handleAnchorClick);
      });
      continue;
    }

    const handleImageClick = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      window.open(resolvedSrc, '_blank', 'noopener,noreferrer');
    };

    image.addEventListener('click', handleImageClick);
    cleanups.push(() => {
      image.removeEventListener('click', handleImageClick);
    });
  }

  return () => {
    cleanups.forEach(cleanup => cleanup());
  };
}

export const ClickableImageLinksAddon = () => {
  const shadowRoot = useShadowRoot();
  const images = useShadowRootElements<HTMLImageElement>(['img']);

  useEffect(() => {
    ensureTechDocsCanvasStyles(shadowRoot);
  }, [shadowRoot]);

  useEffect(() => {
    normalizeTechDocsImageLinks(images);
    return bindTechDocsImageClicks(images);
  }, [images]);

  return null;
};
